import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { liveQuery } from 'dexie';
import { ChevronDown, Send, Trash2 } from 'lucide-react';
import {
  PRESITE_STATUSES, SURFACE_OTHER, SURFACE_TYPES, SURVEY_SECTIONS, formatDateTime, formatDay, statusAfterSurvey, surveyAsked, surveyMissing,
} from '@nu/shared';
import { useDraft } from '../../offline/draft.js';
import { db } from '../../offline/db.js';
import { api } from '../../api/client.js';
import { dropPhoto, queuePhotosTo, shrink, subscribePhotos } from '../../offline/photos.js';
import { outbox } from '../../offline/index.js';
import PhotoButtons from '../../components/PhotoButtons.jsx';
import { currentPosition } from '../../native/device.js';
import { sendSurvey, surveyValues } from './store.js';

// Column 12's two answers, as two buttons (tap again to clear).
const PERMIT_LENGTHS = ['1 day', '3 days'];

// One job's survey. On site (`gps`, the app): the questions of
// SURVEY_SECTIONS (shared/src/surveys.js), after Modular Plus's off site
// survey, section by section as tap buttons, with each section's photos;
// then columns 7-14 of Network Plus's sheet. Everything asked has to be
// answered (and the photos taken, or a reason given) before it can be sent;
// what's left is listed above Submit. In the office (the job's pop-up) it's
// the same form but nothing is required: they're correcting it. What's typed
// is kept on the device until it's sent; sending goes through the outbox
// (works with no signal). The first answer on site notes when, and where the
// phone was.
export default function SurveyForm({ me, job, canEdit, onSent, gps = false, gangs = [], photosOn = true }) {
  const [draft, save, clear] = useDraft(`nu.draft.survey.${job.id}`);
  const start = { ...surveyValues(job), teamId: job.teamId ? String(job.teamId) : '' };
  // Every open team (and the job's own, even if it's since closed).
  const teamList = gangs.filter((g) => g.active || g.teamId === job.teamId);
  const v = { ...start, ...draft };
  const details = { ...(job.details || {}), ...(draft.details || {}) };
  const photos = useSurveyPhotos(job, photosOn);
  const counts = {};
  for (const p of photos.all) counts[p.slot || 'extra'] = (counts[p.slot || 'extra'] || 0) + 1;

  const started = () => {
    if (!gps || draft.startedAt || job.surveyStartedAt) return {};
    currentPosition({ timeout: 8000 }).then((w) => { if (w) save({ startWhere: w }); });
    return { startedAt: new Date().toISOString() };
  };
  const answer = (patch) => save({ ...started(), details: { ...(draft.details || {}), ...patch } });
  const skipPhoto = (slot, reason) => answer({ photoSkip: { ...(details.photoSkip || {}), [slot]: reason } });

  // Surface Type: one of the list, or Other with what they typed. Until it's
  // chosen, the excavation surface answered above.
  const fromSite = SURFACE_TYPES.includes(details.excavationSurface) ? details.excavationSurface : '';
  const knownSurface = SURFACE_TYPES.includes(start.surfaceType);
  const surfaceChoice = draft.surfaceChoice ?? (knownSurface ? start.surfaceType : start.surfaceType ? SURFACE_OTHER : fromSite);
  const surfaceOther = draft.surfaceOther ?? (knownSurface ? '' : start.surfaceType);
  const surfaceValue = surfaceChoice === SURFACE_OTHER ? surfaceOther.trim() : surfaceChoice;
  const needsOther = surfaceChoice === SURFACE_OTHER && !surfaceValue;
  // MST Location: until typed, where the dig is.
  const mstLocation = 'mstLocation' in draft ? draft.mstLocation : (start.mstLocation || details.digLocation || '');
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => save({ [k]: e.target.value });
  const status = statusAfterSurvey(job.jobStatus, v.plannedDate);
  const missing = gps ? surveyMissing(details, counts) : [];
  const blocked = !v.presiteStatus || needsOther || missing.length > 0;

  const submit = async (e) => {
    e.preventDefault();
    if (blocked || busy) return;
    setBusy(true);
    // Where the phone is (on site), if it can tell quickly; never blocks.
    const where = gps ? await currentPosition({ timeout: 6000 }) : null;
    await sendSurvey(me, job, {
      presiteStatus: v.presiteStatus,
      presiteNotes: v.presiteNotes.trim(),
      surfaceType: surfaceValue,
      mstLocation: mstLocation.trim(),
      permitConditions: v.permitConditions.trim(),
      permitLength: v.permitLength.trim(),
      plannedDate: v.plannedDate,
      teamId: v.teamId ? Number(v.teamId) : null,
      details,
      ...(draft.startedAt ? { startedAt: draft.startedAt, startWhere: draft.startWhere || null } : {}),
    }, where);
    clear();
    setBusy(false);
    onSent?.();
  };

  return (
    <form className="stack survey-form" onSubmit={submit}>
      {job.surveyedAt && (
        <p className="small muted">
          {job.sending ? 'Sending…' : 'Surveyed'} by {job.surveyedByName || 'someone'}, {formatDateTime(job.surveyedAt)}. Change anything below and tap Update Survey.
        </p>
      )}
      {SURVEY_SECTIONS.filter((sec) => !sec.showIf || sec.showIf(details)).map((sec) => (
        <fieldset key={sec.id} className="survey-section" disabled={!canEdit}>
          <legend>{sec.title}</legend>
          {(sec.questions || []).filter((q) => surveyAsked({ ...q, section: sec.id }, details)).map((q) => (
            <Question key={q.key} q={q} d={details} onAnswer={answer} />
          ))}
          {photosOn && (sec.photos || []).filter((p) => surveyAsked({ ...p, section: sec.id }, details)).map((p) => (
            <SlotPhotos
              key={p.slot}
              slot={p}
              photos={photos.all.filter((x) => (x.slot || 'extra') === p.slot)}
              urls={photos.urls}
              canEdit={canEdit}
              reason={details.photoSkip?.[p.slot] || ''}
              onReason={(r) => skipPhoto(p.slot, r)}
              onAdd={(files) => photos.add(p.slot, files)}
              onRemove={photos.remove}
            />
          ))}
        </fieldset>
      ))}
      {photos.error && <p className="notice-line notice-bad">{photos.error}</p>}

      <fieldset className="survey-section" disabled={!canEdit}>
        <legend>For Network Plus’s sheet</legend>
        <label className="fld">
          <span>Presite Status</span>
          <select value={v.presiteStatus} onChange={set('presiteStatus')} required>
            <option value="">Choose…</option>
            {PRESITE_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
        <label className="fld">
          <span>Presite Notes</span>
          <GrowText rows={3} maxLength={4000} value={v.presiteNotes} onChange={set('presiteNotes')} placeholder="What you found on site" />
        </label>
        <div className="fld-row">
          <label className="fld">
            <span>Surface Type</span>
            <select value={surfaceChoice} onChange={(e) => save({ surfaceChoice: e.target.value })}>
              <option value="">Choose…</option>
              {[...SURFACE_TYPES, SURFACE_OTHER].map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
            {surfaceChoice === SURFACE_OTHER && (
              <input maxLength={120} value={surfaceOther} onChange={(e) => save({ surfaceOther: e.target.value })} placeholder="Say what the surface is" aria-label="Other surface type" />
            )}
          </label>
          <label className="fld">
            <span>MST Location</span>
            <GrowText rows={1} maxLength={255} value={mstLocation} onChange={set('mstLocation')} />
          </label>
        </div>
        <div className="fld-row">
          <label className="fld">
            <span>Permit conditions required</span>
            <GrowText rows={1} maxLength={255} value={v.permitConditions} onChange={set('permitConditions')} />
          </label>
          <div className="fld">
            <span id={`permit-length-${job.id}`}>Permit length required</span>
            <div className="survey-choice" role="group" aria-labelledby={`permit-length-${job.id}`}>
              {PERMIT_LENGTHS.map((l) => (
                <button
                  key={l}
                  type="button"
                  className={`btn${v.permitLength === l ? ' on' : ''}`}
                  aria-pressed={v.permitLength === l}
                  onClick={() => save({ permitLength: v.permitLength === l ? '' : l })}
                >
                  {l}
                </button>
              ))}
            </div>
            {v.permitLength && !PERMIT_LENGTHS.includes(v.permitLength) && <span className="small muted">From the sheet: {v.permitLength}</span>}
          </div>
        </div>
        <div className="fld">
          <span id={`team-${job.id}`}>Team <span className="small muted">(who will install it)</span></span>
          {teamList.length
            ? <TeamPicker teams={teamList} value={v.teamId} disabled={!canEdit} labelledBy={`team-${job.id}`} onChange={(id) => save({ teamId: id })} />
            : <span className="small muted">No teams yet. Teams are set up on Match-Ups.</span>}
        </div>
        <div className="fld-row">
          <label className="fld">
            <span>Planned Date <span className="small muted">(if the customer gave one)</span></span>
            <input type="date" value={v.plannedDate} onChange={set('plannedDate')} />
          </label>
        </div>
      </fieldset>
      {canEdit && (
        <>
          <p className="small survey-becomes">
            Job Status becomes <strong>{status}</strong>{v.plannedDate ? ` (planned ${formatDay(v.plannedDate)})` : ''}.
          </p>
          {needsOther && <p className="small warn-text survey-becomes">Surface Type is Other: say what the surface is.</p>}
          {missing.length > 0 && (
            <div className="survey-missing">
              <strong>Still to do ({missing.length})</strong>
              <ul>{missing.map((m) => <li key={m}>{m}</li>)}</ul>
            </div>
          )}
          <div className="actions survey-submit">
            <button type="submit" className="btn primary with-icon" disabled={blocked || busy}>
              <Send size={18} aria-hidden="true" /> {job.surveyedAt ? 'Update Survey' : 'Submit Survey'}
            </button>
          </div>
        </>
      )}
    </form>
  );
}

// One question: tap buttons for a choice or Yes/No (tap again to clear), a
// text box, or a number. A choice of Other asks what.
function Question({ q, d, onAnswer }) {
  const id = `sq-${q.key}`;
  const value = d[q.key] ?? '';
  if (q.type === 'text') {
    return (
      <label className="fld">
        <span>{q.label}</span>
        <GrowText rows={2} maxLength={2000} value={value} onChange={(e) => onAnswer({ [q.key]: e.target.value })} placeholder={q.hint || ''} />
      </label>
    );
  }
  if (q.type === 'number') {
    return (
      <label className="fld">
        <span>{q.label}</span>
        <input type="number" inputMode="numeric" min={q.min} max={q.max} value={value} onChange={(e) => onAnswer({ [q.key]: e.target.value === '' ? '' : Number(e.target.value) })} />
      </label>
    );
  }
  const options = q.type === 'yesno' ? ['Yes', 'No'] : q.options;
  return (
    <div className="fld">
      <span id={id}>{q.label}</span>
      <div className={`survey-choice${options.length > 2 ? ' many' : ''}`} role="group" aria-labelledby={id}>
        {options.map((o) => (
          <button key={o} type="button" className={`btn${value === o ? ' on' : ''}`} aria-pressed={value === o} onClick={() => onAnswer({ [q.key]: value === o ? '' : o })}>
            {o}
          </button>
        ))}
      </div>
      {value === 'Other' && (
        <input maxLength={200} value={d[`${q.key}Other`] || ''} onChange={(e) => onAnswer({ [`${q.key}Other`]: e.target.value })} placeholder="Say what" aria-label={`${q.label}: other`} />
      )}
    </div>
  );
}

// A photo slot: its photos, Take photo / Upload, and for one that can be
// missed, "Couldn't take it?" with the reason.
function SlotPhotos({ slot, photos, urls, canEdit, reason, onReason, onAdd, onRemove }) {
  const [why, setWhy] = useState(false);
  const need = slot.need === 'required' ? (slot.min > 1 ? `${slot.min} needed` : 'needed') : slot.need === 'optional' ? 'optional' : '';
  return (
    <div className="survey-slot">
      <span className="survey-slot-label">{slot.label}{need && <span className="small muted"> ({need})</span>}</span>
      {photos.length > 0 && (
        <ul className="photo-picks">
          {photos.map((p, i) => (
            <li key={p.id}>
              {urls[p.id] ? <img src={urls[p.id]} alt={`${slot.label}, photo ${i + 1}`} /> : <span className="muted small">Photo {i + 1}</span>}
              <span className="small muted photo-when">{p.pending ? 'Uploading… ' : ''}{formatDateTime(p.takenAt)}</span>
              {canEdit && (
                <button type="button" className="icon-btn" aria-label={`Remove ${slot.label}, photo ${i + 1}`} onClick={() => onRemove(p)}><Trash2 size={16} aria-hidden="true" /></button>
              )}
            </li>
          ))}
        </ul>
      )}
      {canEdit && <PhotoButtons onFiles={onAdd} multiple title={slot.label} />}
      {slot.need === 'or_reason' && !photos.length && (why || reason
        ? (
          <label className="fld">
            <span className="small">Why couldn’t you take it?</span>
            <input maxLength={300} value={reason} onChange={(e) => onReason(e.target.value)} placeholder="e.g. couldn’t get the cover off" />
          </label>
        )
        : canEdit && <button type="button" className="link-btn small survey-skip" onClick={() => setWhy(true)}>Couldn’t take it?</button>)}
    </div>
  );
}

// A team, picked by searching: shows the chosen one (name, members, gang
// number); tapped, it opens a search box over a short scrolling list that
// matches team name, member names or gang number. Built for 50+ teams.
function TeamPicker({ teams, value, disabled, labelledBy, onChange }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const esc = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [open]);
  const chosen = teams.find((t) => String(t.teamId) === value) || null;
  const words = q.trim().toLowerCase().split(/s+/).filter(Boolean);
  const shown = teams.filter((t) => {
    const hay = `${t.teamName} ${t.members} ${t.gangName || ''}`.toLowerCase();
    return words.every((w) => hay.includes(w));
  });
  const pick = (id) => { onChange(id); setOpen(false); setQ(''); };
  const lines = (t) => (
    <>
      <strong>{t.teamName}</strong>
      <span className="small">{t.members || 'Nobody in the team'}</span>
      <span className="small">Gang Number: {t.gangName ? <strong>{t.gangName}</strong> : <em>not set yet</em>}</span>
    </>
  );
  return (
    <div className="team-select" ref={box}>
      <button type="button" className="team-current" aria-haspopup="listbox" aria-expanded={open} aria-labelledby={labelledBy} disabled={disabled} onClick={() => setOpen(!open)}>
        <span className="team-current-text">{chosen ? lines(chosen) : <span className="muted">Choose a team…</span>}</span>
        <ChevronDown size={18} aria-hidden="true" />
      </button>
      {/* A sheet over the whole screen (a centred box on a computer), so the
          keyboard never covers the list. */}
      {open && (
        <div className="team-sheet-back" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}>
        <div className="team-pop" role="dialog" aria-modal="true" aria-label="Choose a team">
          <div className="team-pop-head">
            <strong>Choose a team</strong>
            <button type="button" className="link-btn" onClick={() => { setOpen(false); setQ(''); }}>Cancel</button>
          </div>
          <input type="search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search team, name or gang number" aria-label="Search teams" />
          <ul className="team-options" role="listbox" aria-labelledby={labelledBy}>
            {chosen && <li><button type="button" className="team-pick clear" onClick={() => pick('')}>No team</button></li>}
            {shown.map((t) => (
              <li key={t.teamId}>
                <button type="button" role="option" aria-selected={String(t.teamId) === value} className={`team-pick${String(t.teamId) === value ? ' on' : ''}`} onClick={() => pick(String(t.teamId))}>
                  {lines(t)}
                </button>
              </li>
            ))}
            {!shown.length && <li className="small muted team-none">No team matches “{q}”.</li>}
          </ul>
        </div>
        </div>
      )}
    </div>
  );
}

// A text box that grows as it's typed in (and shrinks back), never smaller
// than `rows` lines.
function GrowText({ rows, value, ...rest }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight + 2}px`;
  }, [value]);
  return <textarea ref={ref} rows={rows} value={value} className="grow-text" {...rest} />;
}

// The survey's photos: the server's plus any still on this device, each
// with its slot (an older one without a slot counts as 'extra'), and their
// thumbnails. Photos are optional per slot; see SURVEY_PHOTO_SLOTS.
function useSurveyPhotos(job, photosOn) {
  const key = `survey:${job.id}`;
  const base = `/surveys/jobs/${job.id}/photos`;
  const [server, setServer] = useState([]);
  const [local, setLocal] = useState([]);
  const [urls, setUrls] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!photosOn) return undefined;
    let live = true;
    const load = () => api(base).then((l) => { if (live) setServer(l); }, () => {});
    load();
    const off = subscribePhotos(load);
    const offOutbox = outbox.subscribe(load);
    const sub = liveQuery(() => db.photos.where('bookingId').equals(key).toArray()).subscribe({ next: (l) => live && setLocal(l) });
    return () => { live = false; off(); offOutbox(); sub.unsubscribe(); };
  }, [base, key, photosOn]);

  const localIds = new Set(local.map((p) => p.id));
  const all = [
    ...server.filter((p) => !localIds.has(p.id)).map((p) => ({ ...p, pending: false })),
    ...local.map((p) => ({ id: p.id, slot: p.slot || null, takenAt: p.takenAt, blob: p.blob, pending: true })),
  ];
  const ids = all.map((p) => p.id).join(',');
  useEffect(() => {
    let live = true;
    (async () => {
      for (const p of all) {
        if (urls[p.id]) continue;
        const blob = p.blob || await api(`${base}/${p.id}`, { as: 'blob' }).catch(() => null);
        if (!live || !blob) continue;
        const url = URL.createObjectURL(blob);
        setUrls((u) => ({ ...u, [p.id]: url }));
      }
    })();
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  const add = async (slot, files) => {
    setError(null);
    try {
      const blobs = [];
      for (const f of files) blobs.push(await shrink(f, { label: job.jobNumber }));
      await queuePhotosTo(key, base, blobs, new Date().toISOString(), slot);
    } catch (e) {
      setError(e.message);
    }
  };
  const remove = async (p) => {
    if (p.pending) await dropPhoto(p.id);
    else await outbox.enqueue('survey.photo.remove', { jobId: job.id, photoId: p.id });
    setServer((s) => s.filter((x) => x.id !== p.id));
  };
  return { all: photosOn ? all : [], urls, add, remove, error };
}
